import {AbstractStore} from "./abstract-store";
import {action, autorun, computed, makeObservable, observable} from "mobx";
import {ApiLayerConfiguration} from "../config/auftragsverwaltung-api-configuration";
import {JsonSerializer, throwError} from "typescript-json-serializer";
import {Project} from "../models/project";
import {Difference} from "../models/difference";
import {ProjectMunicipality, ProjectMunicipalityProps} from "../models/project-municipality";
import {JournalStore} from "./journal-store-mobx";

export interface ProjectMunicipalityStoreProps {
    layerConfig: ApiLayerConfiguration;
    journalStore: JournalStore;
}

export class ProjectMunicipalityStore extends AbstractStore {

    journalStore: JournalStore | undefined;

    /**
     * Loaded items (local store)
     */
    items = observable.map<string, ProjectMunicipality>();

    constructor(props: ProjectMunicipalityStoreProps) {
        super({ layerConfig: props.layerConfig });

        this.journalStore = props.journalStore;

        makeObservable(this, {
            status: observable,
            dataStatus: observable,

            items: observable,
            selectedItem: observable,
            setSelectedItem: action,
            resetSelectedItem: action,
            visibleItems: computed,

            addItem: action,
            removeItem: action,

            isDataLoading: computed,
            isDataLoaded: computed,
            isDataError: computed,
            isLoading: computed,
            isLoaded: computed,
            isNotFound: computed,
            isError: computed
        });
    }

    get visibleItems(): ProjectMunicipality[] {
        // apply filters
        const filtered = [...this.items.values()].filter(() => {
            return true;
        });
        return [ ...filtered];
    }

    /**
     * Request from server and store in local store
     * @param projectIds (Optional) IDs of projects
     */
    loadItems(projectIds?: number[]): Promise<ProjectMunicipality[]> {
        return new Promise((resolve) => {
            this.setDataStatus("loading");

            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose();

                this.queryFeatures(projectIds)
                    .then((projectMunicipalities: ProjectMunicipality[]) => {
                        if (projectMunicipalities) {
                            // const map = observable.map<string, ProjectMunicipality[]>();
                            // projectMunicipalities.forEach((pm: ProjectMunicipality) => {
                            //     if (map.get(pm.projectId)) {
                            //         map.get(pm.projectId)!.push(pm);
                            //     } else {
                            //         map.set(pm.projectId, [pm]);
                            //     }
                            // });
                            //this.addItem(map);

                            this.setDataStatus("loaded");
                            resolve(projectMunicipalities);
                        }
                    });
            });
        });
    }

    /**
     * Save to the server
     *
     * @param project
     * @param toAdd
     * @param toDelete
     * @param user
     */
    save(project: Project,
         toAdd: ProjectMunicipality[],
         toDelete: ProjectMunicipality[] | number[],
         user: string): Promise<null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!project) {
                    reject("Project undefined when saving municipality edits.");
                    return;
                }

                const idDiff = new Difference({
                    p: "bfsNr",
                    o: toDelete.map((pm: ProjectMunicipality | number) => pm instanceof ProjectMunicipality ? pm.bfsNr : pm).join(", "),
                    n: toAdd.map((pm) => pm.bfsNr).join(", ")}
                );
                this.journalStore?.journalizeItem(
                    project,
                    "project-municipality",
                    "update",
                    user,
                    [idDiff],
                    "Zuweisung Gemeinde geändert."
                );

                const featuresToAdd = toAdd.map((pm) => {
                    pm.feature = this.createFeature(null, pm);
                    if (!pm?.feature) {
                        reject(`Feature undefined for project municipality ${pm.objectId}`);
                    }
                    return pm.feature;
                });
                // TODO: do not convert when OBJECTIDS
                const isObjectIds = toDelete.some((pm) => typeof pm !== "object" && Number.isInteger(pm));
                let deletable;
                if (isObjectIds) {
                    deletable = toDelete as number[];
                }
                else {
                    deletable = (toDelete as ProjectMunicipality[]).map((pm: ProjectMunicipality) => {
                        pm.feature = this.createFeature(null, pm);
                        if (!pm?.feature) {
                            reject(`Feature undefined for project municipality ${pm.objectId}`);
                        }
                        return pm.feature;
                    });
                }

                this.saveFeatures(this.layer, user, featuresToAdd, undefined, deletable).then(() => {
                    resolve(null);
                }, (error: any) => {
                    console.error("Error saving project municipalities", error);
                    reject(error);
                });
            });
        });
    }

    /**
     * Delete a project municipality
     * @param project
     * @param projectMunicipality
     * @param user
     */
    delete(project: Project,
           projectMunicipality: ProjectMunicipality,
           user: string): Promise<number | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!project) {
                    reject("project undefined when saving municipality edits.");
                    return;
                }
                if (!projectMunicipality) {
                    reject("project municipality undefined when saving edits.");
                    return;
                }

                const feature = projectMunicipality?.feature;
                if (!feature) {
                    reject(`Feature undefined for project municipality ${projectMunicipality.objectId}`);
                    return;
                }

                this.deleteFeature(feature).then((objectId: number) => {
                    // log deletion in journal
                    const nameDiff = new Difference({p: "name", o: project.name || ""});
                    const idDiff = new Difference({p: "id", o: project.id?.toString()});
                    this.journalStore?.journalizeItem(
                        project,
                        "project",
                        "delete",
                        user,
                        [idDiff, nameDiff],
                        "Gemeinde gelöscht"
                    );

                    resolve(objectId);
                });
            });
        });
    }

    queryFeatures(projectIds?: number[] | undefined): Promise<ProjectMunicipality[] | [] | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose();

                // Get a query object for the layer's current configuration
                // queryParams.outFields will be set to ["*"] to get values
                // for all available fields.
                const queryParams = this.layer.createQuery();

                // load all features if no journal guid is specified
                if (projectIds !== undefined) {
                    // Add to the layer's current definitionExpression
                    queryParams.where += ` AND PROJ_ID IN (${projectIds.join(",")})`;
                }

                // query the layer with the modified params object
                this.layer.queryFeatures(queryParams).then((results: __esri.FeatureSet) => {
                    if (results.features) {
                        const projectMunicipalities = this.parseQueryResult(results);
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        resolve(projectMunicipalities);
                    } else {
                        reject();
                    }
                }, (error) => {
                    throw new Error(`Error querying project municipalities. ${error}`);
                });
            });
        });
    }

    private parseQueryResult(data: __esri.FeatureSet): ProjectMunicipality[] {
        const serializer = new JsonSerializer({
            errorCallback: throwError
        });

        const projectMunicipalities: ProjectMunicipality[] = [];
        data.features?.forEach((feature: any) => {
            const json = serializer.deserialize(feature.attributes, ProjectMunicipality);
            const projectMunicipality = new ProjectMunicipality(json as ProjectMunicipalityProps);
            projectMunicipality.feature = feature;

            projectMunicipalities.push(projectMunicipality);
        });
        return projectMunicipalities;
    }

}
